<template>
  <div
    id="product-form"
    class="product-form"
    :class="{ [$class('loading')]: loading }"
    data-sign-post-location="main"
    js-sign-post
  >
    <form
      class="product-form__form form-group"
      :class="{ [$class('error')]: error }"
      :disabled="isOutOfStock"
      @submit.prevent="handleAddToCart"
    >
      <product-options
        v-show="showProductOptions"
        class="product-form__options"
        :loading="loading"
        :product="product"
        :section="section"
        :variant="variant"
        @update-variant="handleUpdateVariant"
      />

      <input
        name="quantity"
        readonly
        type="hidden"
        :value="quantity"
      >

      <div class="form-group__field">
        <div
          class="product-form__add-to-cart-container"
          :class="{ [$class('active')]: isFloating }"
        />

        <btn
          ref="addToCartButton"
          class="product-form__add-to-cart"
          :class="{ [$class('fixed')]: isFloating }"
          :disabled="isOutOfStock"
          :label="addToCartText"
          modifiers="block center yellow"
          :show-state-icon="state === 'adding'"
          type="submit"
        >
          <template #stateIcon>
            <icon-loading />
          </template>
        </btn>

        <p
          v-show="error"
          class="form-group__note text-body-s-regular"
          role="status"
          v-text="error"
        />
      </div>

      <recipient-form
        v-if="product.giftCard"
        class="product-form__recipient-form"
        :gift-card="giftCard"
        :section="section"
        @update-recipient-form="handleUpdateRecipientForm"
      />
    </form>

    <div
      v-if="isPreviewEligible"
      class="product-form__preview"
    >
      <btn
        ref="previewButton"
        :label="isMobile ? $string('product.preview.mobile') : $string('product.preview.desktop')"
        modifiers="block center secondary"
        :show-state-icon="previewState === 'loading'"
        :target="previewUrl ? '_blank' : null"
        :type="previewUrl ? null : 'button'"
        :url="previewUrl"
        @click="setRemoteContentLink"
      >
        <template #stateIcon>
          <icon-loading />
        </template>
      </btn>
    </div>

    <p
      v-if="previewMessage"
      class="product-form__preview-message text-body-xs-regular"
      :class="{ 'product-form__preview-message--error': previewState === 'error' }"
      v-text="previewMessage"
    />

    <div ref="observerElement" />
  </div>
</template>

<script>

/**
 * Vue: Product form (product-form)
 * -----------------------------------------------------------------------------
 * Product form to handle variant selection and adding to cart.
 *
 * @param {Object} [autofixCategories] - Vehicle type to autofix category
 * mapping from theme settings.
 * @param {Boolean} [loading] - Loading state.
 * @param {Boolean|Object} product - Canvas formatted product object.
 * @param {Object} section - Main product section settings.
 * @param {Boolean|Null|Object} variant - Canvas formatted variant object.
 *
 * @emits updateVariant - Emit new variant object.
 *
 */
import { mapActions, mapGetters, mapState } from 'vuex'

import Btn from '~global/btn/btn'
import ProductOptions from '~async/product-form/product-options'
import RecipientForm from '~async/product-form/recipient-form'

import IconLoading from '~icons/general/loading.svg'

import {
  formatProductForBloomreachreach,
  trackBloomreachEvent,
} from '~/helpers/bloomreach'
import { fetchApiData } from '~/helpers/garage'
import { setIntersectionObserver } from '~/helpers/general'
import { emitUpdateVariant } from '~/helpers/vue'

export default {
  name: 'ProductForm',

  components: {
    Btn,
    IconLoading,
    ProductOptions,
    RecipientForm,
  },

  props: {
    autofixCategories: {
      type: Object,
      default: () => ({
        axles: [],
        cars: [],
        motorcycles: [],
        trailers: [],
        trucks: [],
      }),
    },
    loading: {
      type: Boolean,
      default: false,
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    section: {
      type: Object,
      default: () => ({
        autoOpenCartDrawer: true,
        displayRecipientForm: true,
        displaySingleOptionNames: true,
        displaySingleValueOptions: false,
        displaySingleVariantOption: false,
        dropdownOptions: [],
        excludedInitialSelectOptions: ['length', 'size', 'waist', 'width'],
        hideGiftCardOptionNames: true,
        id: '',
        initialSelect: true,
        resetFormBehaviour: 'disabled',
        resetRecipientFormOnAdd: true,
        selectDisabledValues: true,
      }),
      required: true,
    },
    variant: {
      type: [Boolean, null, Object],
      default: false,
      required: true,
    },
  },

  emits: {
    'update-variant': emitUpdateVariant,
  },

  data() {
    return {
      error: '',
      giftCard: {
        enableForm: false,
        fields: {
          Message: '',
          'Recipient email': '',
          'Recipient name': '',
          'Send on': '',
        },
        timezoneOffset: '',
      },
      lineItemProperties: {},
      previewMessage: '',
      previewState: 'ready',
      previewUrl: '',
      quantity: 1,
      state: 'ready',
      timeout: {},
      observer: null,
      isFloating: false,
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getComponentExists: 'components/getExists',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute add to cart text.
     * @returns {String}
     */
    addToCartText() {
      if (!this.variant) {
        return this.product.giftCard
          ? this.$string('product.cta.select_gift')
          : this.$string('product.cta.select_variant')
      }

      if (this.isOutOfStock) {
        return this.$string('product.cta.sold_out')
      }

      return this.state === 'added'
        ? this.$string('product.cta.added_to_cart')
        : this.$string('product.cta.add_to_cart')
    },

    /**
     * Compute autofix vehicle category.
     * @returns {String}
     */
    autofixVehicleCategory() {
      const vehicleType = this.product?.metafields?.vehicleType?.[0]

      if (this.autofixCategories.cars.includes(vehicleType)) {
        return 'cars'
      }

      if (this.autofixCategories.motorcycles.includes(vehicleType)) {
        return 'motorcycles'
      }

      if (this.autofixCategories.trucks.includes(vehicleType)) {
        return 'trucks'
      }

      if (this.autofixCategories.trailers.includes(vehicleType)) {
        return 'trailers'
      }

      if (this.autofixCategories.axles.includes(vehicleType)) {
        return 'axles'
      }

      return 'cars'
    },

    /**
     * Compute gift card line item properties.
     * @returns {Null|Object}
     */
    giftCardLineItemProperties() {
      if (
        !this.product.giftCard ||
        this.giftCard.fields['Recipient email'] === ''
      ) {
        return null
      }

      const filteredFields = {}

      Object.entries(this.giftCard.fields).forEach(([key, value]) => {
        if (!value) {
          return
        }

        filteredFields[key] = value
      })

      return {
        __shopify_send_gift_card_to_recipient: 'on',
        __shopify_offset: this.giftCard.timezoneOffset,
        ...filteredFields,
      }
    },

    /**
     * Return if the current variant is eligible for preview.
     * @returns {Boolean}
     */
    isPreviewEligible() {
      if (!this.variant || !Array.isArray(this.variant.selectedOptions)) {
        return false
      }
      const previewTypes = this.$variable('settings.previewEligibleProductTypes') || []

      return this.variant.selectedOptions.some(option => previewTypes.includes(option.value)) ||
        (this.product && previewTypes.includes(this.product.type))
    },

    /**
     * Return if the current variant is out of stock.
     * @returns {Boolean}
     */
    isOutOfStock() {
      return !this.variant?.available
    },

    /**
     * Compute show product form element.
     * @returns {Boolean}
     */
    showProductOptions() {
      if (this.loading && !this.product) {
        return true
      }

      const multipleOptions = this.product?.options?.some((option) => {
        return option.values.length > 1
      })

      /**
       * If multiple variants then ensure they have more than one option.
       */
      const showOptionsMultiVariant =
        !this.product.hasOnlyDefaultVariant &&
        multipleOptions

      /**
       * If single variant then check if section settings enable display.
       */
      const showOptionsSingleVariant =
        this.section.displaySingleValueOptions &&
        this.section.displaySingleVariantOption

      return showOptionsMultiVariant || showOptionsSingleVariant
    },
  },

  watch: {

    /**
     * Watch product to reset error.
     */
    product() {
      this.error = ''
      this.previewMessage = ''
      this.previewUrl = ''
    },

    /**
     * Watch variant to reset error.
     */
    variant() {
      this.error = ''
      this.previewMessage = ''
      this.previewUrl = ''
    },
  },

  beforeMount() {
    this.giftCard.timezoneOffset = new Date().getTimezoneOffset().toString()
  },

  mounted() {
    this.setIntersectionObserver()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      addItem: 'cart/addItem',
      openOverlay: 'overlays/open',
    }),

    /**
     * Handle add to cart submission.
     */
    async handleAddToCart() {
      if (!this.variant) {
        this.error = this.product.giftCard
          ? this.$string('product.form.select_gift')
          : this.$string('product.form.select_variant')
        return
      }

      clearTimeout(this.timeout)
      this.error = ''
      this.state = 'adding'

      if (this.$variable('storybook')) {
        if (!this.variant.available) {
          this.error = this.$string('product.form.sold_out')
          this.state = 'ready'
          return
        }

        this.timeout = setTimeout(() => {
          this.state = 'added'
        }, this.$timing('slow'))

        this.timeout = setTimeout(() => {
          this.state = 'ready'
        }, this.$timing('message'))

        return
      }

      this.setLineItemProperties()

      const items = [
        {
          id: this.variant.id,
          properties: this.product.giftCard
            ? {
              ...this.giftCardLineItemProperties,
              ...this.lineItemProperties,
            }
            : this.lineItemProperties,
          quantity: this.quantity,
        },
      ]

      try {
        await this.addItem(items)
        this.state = 'added'

        /**
         * Bloomreach tracking events.
         * - `cart_update` is used to trigger a wider range of scenarios.
         */
        trackBloomreachEvent(
          'add item to cart',
          formatProductForBloomreachreach(this.product, this.variant),
        )

        if (
          this.section.autoOpenCartDrawer &&
          this.getComponentExists('cart-drawer')
        ) {
          this.openOverlay({
            component: 'cart-drawer',
            ignoreDismissed: true,
            namespace: 'cartDrawer',
          })
        }

        await this.resetState()

      } catch (error) {
        this.state = 'error'

        if (error.description) {
          this.handleErrorMessage(error.description)
          return
        }

        cnvs.ReportError('Failed to add product to cart', error)
      }
    },

    /**
     * Set product line items.
     */
    setLineItemProperties() {
      if (this.product.giftCard || !this.variant) {
        return
      }

      if (this.variant.metafields.remoteContentType) {
        // eslint-disable-next-line no-underscore-dangle
        this.lineItemProperties._product_format = this.variant.metafields.remoteContentType
        return
      }

      const formatOption = this.variant.selectedOptions.find((option) => {
        return option.name === 'Type' || option.name === 'Title'
      })

      if (!formatOption) {
        return
      }

      // eslint-disable-next-line no-underscore-dangle
      this.lineItemProperties._product_format = formatOption.value
    },

    /**
     * Reset product form state after adding to cart after message delay.
     */
    async resetState() {
      await cnvs.Sleep(this.$timing('message'))
      this.state = 'ready'

      /**
       * Reset gift card recipient form based on setting.
       */
      if (this.product.giftCard && this.section.resetRecipientFormOnAdd) {
        this.giftCard = {
          enableForm: false,
          fields: {
            Message: '',
            'Recipient email': '',
            'Recipient name': '',
            'Send on': '',
          },
        }
      }

      /**
       * Reset variant selection based on setting.
       */
      if (this.section.resetFormBehaviour !== 'clear') {
        return
      }

      this.handleUpdateVariant(false)
    },

    /**
     * Handle update variant event.
     * @param {Boolean|Null|Object} variant - Emitted variant.
     */
    handleUpdateVariant(variant) {
      this.$emit('update-variant', variant)
    },

    /**
     * Handle update recipient form event.
     * @param {Object} recipientForm - Emitted recipient form.
     */
    handleUpdateRecipientForm(recipientForm) {
      this.giftCard = recipientForm
    },

    /**
     * Handle error message.
     * @param {Object} errorDescription - Error description from add request.
     */
    handleErrorMessage(errorDescription) {
      if (typeof errorDescription === 'string') {
        this.error = errorDescription
        return
      }

      this.error = Object.values(errorDescription)
        .flat()
        .map((error) => {
          return error.endsWith('.') ? error : `${error}.`
        })
        .join('<br>')
    },

    /**
     * Set remote content link.
     * - Duplicate changes from online-products.vue
     * @param {Object} event - Click event.
     */
    async setRemoteContentLink(event) {
      if (this.previewUrl) {
        return
      }

      event.preventDefault()
      let response = {}
      this.previewMessage = ''
      this.previewState = 'loading'
      this.previewUrl = ''

      /**
       * Get remote content link from API.
       */
      try {
        response = await this.getRemoteContentLink()

      } catch (error) {
        this.previewMessage = this.$string('product.preview.error')
        this.previewState = 'error'

        this.trackError('remote', error)
        cnvs.ReportError('Failed to get remote content link', error.message)
      }

      /**
       * Append `chilton_user` and `redirect_url` parameters.
       */
      try {
        const urlParameters = `chilton_user=${this.product.vendor === 'Chilton'}&redirect_url=${this.product.url}`
        this.previewUrl = `${response.response}&${urlParameters}`
        this.previewState = 'ready'

      } catch (error) {
        this.previewMessage = this.$string('product.preview.error')
        this.previewState = 'error'

        this.trackError('parameters', error)
        cnvs.ReportError('Failed to set parameters', error.message)
      }

      /**
       * Open URL by clicking submit button programmatically.
       * - Do not display normal error message if it fails.
       */
      try {
        await this.$nextTick()
        this.previewMessage = this.$string('product.preview.open_message')
        this.$refs.previewButton?.$el?.click()

      } catch (error) {
        this.previewMessage = this.$string('product.preview.open_error')
        this.previewState = 'error'

        this.trackError('opening', error)
        cnvs.ReportError('Failed to open product', error.message)
      }
    },

    /**
     * Request remote content preview link.
     * @returns {Promise<Object>}
     */
    getRemoteContentLink() {
      return new Promise(async(resolve, reject) => {
        try {
          let remoteContentId = this.variant.metafields.remoteContentId

          if (this.variant.metafields.remoteContentType === 'autofix') {
            remoteContentId = this.variant.metafields.remoteContentId.split(':')[0]
          }

          const response = await fetchApiData({
            bodyData: {
              customerId: this.$variable('customer.id') || 'logged-out',
              remoteContentId,
              sku: this.variant.sku,
              type: this.variant.metafields.remoteContentType,
              vehicleCategory: this.autofixVehicleCategory,
              name: 'remote-preview-content',
            },
            ignoreCache: true,
            secretKey: this.$variable('credentials.customApis.secretKey'),
            url: this.$variable('credentials.customApis.remotePreviewContent'),
          })

          if (response.error) {
            reject(response)
            return
          }

          resolve(response)

        } catch (error) {
          reject(error)
        }
      })
    },

    /**
     * Track error.
     * - Emits error to dataLayer for GTM tracking.
     * @param {String} type - Type of error.
     * @param {Object} error - try...catch error object.
     */
    trackError(type, error) {
      const customerAccountId = this.$variable('customer.loggedIn')
        ? this.$variable('customer.id')
        : null

      let remoteContentId = this.variant.metafields?.remoteContentId

      if (this.variant.metafields.remoteContentType === 'autofix') {
        remoteContentId = this.variant.metafields?.remoteContentId.split(':')[0]
      }

      const date = new Date()

      const parameters = {
        customer_account_id: customerAccountId,
        digital_product_sku: this.variant.sku,
        error: error.toString(),
        error_type: type,
        haynes_pro_type_id: remoteContentId,
        timestamp: date.toString(),
      }

      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({
        event: 'digital_product_preview_error',
        ...parameters,
      })
    },

    /**
     * Set IntersectionObserver to observe the observer element.
     */
    setIntersectionObserver() {
      const observerElement = this.$refs.observerElement
      const addToCartButton = this.$refs.addToCartButton

      if (
        !observerElement ||
        !addToCartButton ||
        'IntersectionObserver' in window === false
      ) {
        return
      }

      setIntersectionObserver({
        callback: this.handleIntersection,
        element: observerElement,
        rootMargin: '0px',
        threshold: 0,
      })
    },

    /**
     * Handle intersection.
     * @param {Array} entries - Intersection entries.
     */
    handleIntersection(entries) {
      entries.forEach((entry) => {
        if (entry.isIntersecting || entry.boundingClientRect.top > 0) {
          this.isFloating = false
          return
        }

        this.isFloating = true
      })
    },
  },
}
</script>

<style lang="scss">
@import './product-form';
</style>
